import { supabase } from '../../lib/supabase';
import { ArquivoGrandeDemais, BALDE_DO_CATALOGO, higienizar, recusaPorTamanho } from '../armazenamento';
import { LIMITE_DO_ARQUIVO_BYTES } from '../../constants/maestra';

// OS ARQUIVOS DE UM PERFIL, como a tela de Arquivos os vê.
//
// A lista vem inteira do servidor (`public.arquivos_do_artista`), e não de uma varredura do
// balde: `storage.list()` não é recursivo e a árvore tem quatro níveis, o que daria dezenas de
// chamadas por perfil. Mais importante, a RPC soma o MESMO `metadata->>'size'` que o gatilho
// acumula em `storage_usage` — é isso que faz o total desta tela bater com a barra de consumo.
// Somar pelas tabelas do catálogo daria outro número, e uma tela de cota que contradiz a barra
// é pior do que nenhuma tela.

export type CategoriaDeArquivo = 'audio' | 'capa' | 'pista' | 'guia' | 'imagem' | 'video' | 'documento' | 'outro';

/**
 * O balde dos arquivos que a pessoa envia pelo módulo Arquivos — de qualquer tipo, e PRIVADO
 * (migração 20260924070000). O `catalog` é público e só aceita áudio e imagem: abri-lo para
 * qualquer tipo poria HTML e executável em endereço público nosso.
 */
export const BALDE_DE_ARQUIVOS = 'arquivos';

/**
 * O balde dos anexos do módulo Shows (migração 20260925150000): privado, e de quem vê o show
 * (titular ou membro ativo, `20260925170000`). Aparece nesta tela para a soma bater com a barra de
 * consumo, e não pode ser apagado nem renomeado por aqui: o show aponta para o arquivo.
 */
export const BALDE_DOS_SHOWS = 'shows';

/**
 * O balde das referências visuais do branding (migração 20261006120000): privado, de quem vê o
 * Marketing. Aparece nesta tela para a soma bater com a barra; a referência se tira no branding.
 */
export const BALDE_DO_BRANDING = 'branding';

export type BaldeDoArquivo = typeof BALDE_DO_CATALOGO | typeof BALDE_DE_ARQUIVOS | typeof BALDE_DOS_SHOWS | typeof BALDE_DO_BRANDING;

/** Os baldes privados baixam e mostram por link assinado; o `catalog` é público. */
const privado = (balde: BaldeDoArquivo) => balde !== BALDE_DO_CATALOGO;

const baldeDaLinha = (balde?: string | null): BaldeDoArquivo =>
  balde === BALDE_DE_ARQUIVOS ? BALDE_DE_ARQUIVOS : balde === BALDE_DOS_SHOWS ? BALDE_DOS_SHOWS : balde === BALDE_DO_BRANDING ? BALDE_DO_BRANDING : BALDE_DO_CATALOGO;

export interface ArquivoDoPerfil {
  /** O caminho dentro do balde. É por ele que se apaga. */
  caminho: string;
  nome: string;
  bytes: number;
  mime: string | null;
  criadoEm: string;
  /** Alguma linha do catálogo aponta para este arquivo. */
  emUso: boolean;
  categoria: CategoriaDeArquivo;
  /** A música a que o arquivo pertence, quando dá para saber. */
  musica: string | null;
  /**
   * O projeto (a música) dono do arquivo: é a pasta em que ele aparece na tela.
   *
   * Sai do CAMINHO — `<artista>/<projeto>/versions/...` —, e só quando a RPC achou a música
   * (`musica` preenchida). Assim a pasta nunca nasce de um segmento que não é projeto, como
   * `<artista>/covers/...`. Dois projetos podem ter o mesmo título ("Rascunho"); o id é o que os
   * separa.
   */
  projetoId: string | null;
  /** Em que balde mora. Decide como se baixa (link público ou assinado) e se dá para renomear. */
  balde: BaldeDoArquivo;
  /**
   * Pode ser apagado agora. No catálogo, só o que nenhuma música usa (a policy
   * `catalog_delete_sem_uso` recusaria o resto); no balde `arquivos`, sempre.
   */
  apagavel: boolean;
  /**
   * Pode ser renomeado. Só no balde `arquivos`: um arquivo do catálogo é apontado pela URL, e
   * mudar o nome dele deixaria a música tocando um endereço morto.
   */
  renomeavel: boolean;
}

interface LinhaDaRpc {
  caminho: string;
  nome: string | null;
  bytes: number | string | null;
  mime: string | null;
  criado_em: string;
  em_uso: boolean | null;
  categoria: string | null;
  musica: string | null;
  balde?: string | null;
  projeto_id?: string | null;
}

/** Já vem ordenada do maior para o menor: é a ordem que serve a quem quer liberar espaço. */
export async function arquivosDoArtista(artistId: string): Promise<ArquivoDoPerfil[]> {
  const { data, error } = await supabase.rpc('arquivos_do_artista', { p_artist_id: artistId });
  if (error) throw error;
  return ((data as LinhaDaRpc[] | null) ?? []).map((l) => ({
    caminho: l.caminho,
    nome: l.nome || l.caminho.split('/').pop() || l.caminho,
    // `bigint` chega como string no supabase-js: sem o Number a soma vira concatenação de texto.
    bytes: Number(l.bytes ?? 0),
    mime: l.mime,
    criadoEm: l.criado_em,
    emUso: l.em_uso === true,
    categoria: (l.categoria as CategoriaDeArquivo) || 'outro',
    musica: l.musica,
    // Da RPC: pelo caminho OU, no caso da capa (gravada em `<artista>/covers/`), pela música que a
    // usa como `cover_image`. Ver a migração 20260924080000.
    projetoId: l.projeto_id ?? null,
    balde: baldeDaLinha(l.balde),
    // No `shows`, `em_uso` é "o show ainda existe": o anexo se apaga pelo show.
    apagavel: l.balde === BALDE_DE_ARQUIVOS || l.em_uso !== true,
    renomeavel: l.balde === BALDE_DE_ARQUIVOS,
  }));
}

/**
 * Envia um arquivo qualquer para o perfil — o botão (+) do módulo.
 *
 * ⚠️ SEMPRE NA RAIZ, solto (decisão do Lucas, 24/09/2026): a pasta de uma música guarda o que
 * entrou PELA música (capa, guia, pistas, letra, ficha); o que a pessoa envia pelo (+) é dela, e
 * fica fora das pastas. A cota é conferida pelo banco (`fn_storage_quota_guard`).
 */
export async function enviarArquivoDoPerfil(
  artistId: string,
  arquivo: { nome: string; tipo?: string; dados: Blob | ArrayBuffer; tamanho: number },
): Promise<string> {
  if (arquivo.tamanho > LIMITE_DO_ARQUIVO_BYTES) throw new ArquivoGrandeDemais(arquivo.nome);
  const caminho = `${artistId}/${Date.now()}_${higienizar(arquivo.nome)}`;
  const { error } = await supabase.storage.from(BALDE_DE_ARQUIVOS).upload(caminho, arquivo.dados, {
    upsert: false,
    contentType: arquivo.tipo || 'application/octet-stream',
    // O caminho perde acento e espaço (`higienizar`); o nome de verdade vai junto, e a lista o
    // mostra enquanto o arquivo estiver neste caminho (ver `arquivos_do_artista`).
    metadata: { nome: arquivo.nome.slice(0, 200), caminho },
  });
  if (error) throw recusaPorTamanho(error) ? new ArquivoGrandeDemais(arquivo.nome) : error;
  // O caminho volta para quem enviou de dentro de uma pasta pôr o arquivo nela.
  return caminho;
}

/**
 * Renomeia um arquivo do balde `arquivos`. O carimbo de tempo da frente é renovado, e é o que
 * garante que o nome novo nunca colida com outro da mesma pasta.
 */
export async function renomearArquivoDoPerfil(arquivo: ArquivoDoPerfil, novoNome: string): Promise<void> {
  if (!arquivo.renomeavel) throw new Error('Este arquivo não pode ser renomeado.');
  const nome = novoNome.trim();
  if (!nome) throw new Error('O nome não pode ficar vazio.');
  // A extensão fica: sem ela o sistema de quem baixa não sabe com que programa abrir.
  const extensao = arquivo.nome.match(/\.[a-z0-9]{1,8}$/i)?.[0] ?? '';
  const final = extensao && !nome.toLowerCase().endsWith(extensao.toLowerCase()) ? `${nome}${extensao}` : nome;
  const pasta = arquivo.caminho.slice(0, arquivo.caminho.lastIndexOf('/'));
  const { error } = await supabase.storage
    .from(BALDE_DE_ARQUIVOS)
    .move(arquivo.caminho, `${pasta}/${Date.now()}_${higienizar(final)}`);
  if (error) throw error;
}

/**
 * O link para baixar. No `catalog` (público), a URL pública; no `arquivos` (privado), um link
 * assinado de 5 minutos. Os dois forçam download com o nome que a pessoa vê.
 */
export async function linkParaBaixar(arquivo: ArquivoDoPerfil): Promise<string> {
  if (privado(arquivo.balde)) {
    const { data, error } = await supabase.storage
      .from(arquivo.balde)
      .createSignedUrl(arquivo.caminho, 300, { download: arquivo.nome });
    if (error || !data) throw error ?? new Error('Não foi possível gerar o link.');
    return data.signedUrl;
  }
  return supabase.storage.from(BALDE_DO_CATALOGO).getPublicUrl(arquivo.caminho, { download: arquivo.nome }).data.publicUrl;
}

/** Arquivo que se abre no editor de texto (e não se baixa): o `.txt`/`.md` enviado pelo (+). */
export const ehTextoEditavel = (arquivo: Pick<ArquivoDoPerfil, 'balde' | 'nome' | 'mime'>): boolean =>
  arquivo.balde === BALDE_DE_ARQUIVOS
  && (/\.(txt|md)$/i.test(arquivo.nome) || arquivo.mime === 'text/plain' || arquivo.mime === 'text/markdown');

/** Documento do Word que o editor abre com formatação (ver `documentoDocx` na web). */
export const ehDocx = (arquivo: Pick<ArquivoDoPerfil, 'balde' | 'nome' | 'mime'>): boolean =>
  arquivo.balde === BALDE_DE_ARQUIVOS
  && (/\.docx$/i.test(arquivo.nome) || arquivo.mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');

/**
 * O endereço para TOCAR ou MOSTRAR (sem forçar download). No `catalog`, a URL pública; no
 * `arquivos`, um link assinado de 1 hora — o bastante para ouvir uma faixa inteira sem o link
 * morrer no meio.
 */
export async function linkParaTocar(arquivo: Pick<ArquivoDoPerfil, 'balde' | 'caminho'>): Promise<string> {
  if (privado(arquivo.balde)) {
    const { data, error } = await supabase.storage.from(arquivo.balde).createSignedUrl(arquivo.caminho, 3600);
    if (error || !data) throw error ?? new Error('Não foi possível abrir o arquivo.');
    return data.signedUrl;
  }
  return supabase.storage.from(BALDE_DO_CATALOGO).getPublicUrl(arquivo.caminho).data.publicUrl;
}

/**
 * As miniaturas das imagens de uma lista, por caminho. As do balde privado saem num pedido só
 * (`createSignedUrls`), e não num por imagem.
 */
export async function linksDasMiniaturas(arquivos: ArquivoDoPerfil[]): Promise<Record<string, string>> {
  const imagens = arquivos.filter((a) => a.categoria === 'capa' || a.categoria === 'imagem');
  const links: Record<string, string> = {};
  for (const a of imagens.filter((x) => x.balde === BALDE_DO_CATALOGO)) {
    links[a.caminho] = supabase.storage.from(BALDE_DO_CATALOGO).getPublicUrl(a.caminho).data.publicUrl;
  }
  // Um pedido por balde privado, e não um por imagem.
  for (const balde of [BALDE_DE_ARQUIVOS, BALDE_DOS_SHOWS, BALDE_DO_BRANDING] as const) {
    const privadas = imagens.filter((x) => x.balde === balde).map((x) => x.caminho);
    if (!privadas.length) continue;
    const { data } = await supabase.storage.from(balde).createSignedUrls(privadas, 3600);
    for (const item of data ?? []) if (item.path && item.signedUrl) links[item.path] = item.signedUrl;
  }
  return links;
}

/**
 * O arquivo inteiro, como `Blob`. É como o PDF abre no visualizador: servido de um endereço local
 * (`URL.createObjectURL`), ele não depende de o Storage deixar ser embutido noutra página.
 */
export async function blobDoArquivo(arquivo: Pick<ArquivoDoPerfil, 'balde' | 'caminho'>): Promise<Blob> {
  const { data, error } = await supabase.storage.from(arquivo.balde).download(arquivo.caminho);
  if (error || !data) throw error ?? new Error('Não foi possível abrir o arquivo.');
  return data;
}

/** O conteúdo de um `.txt`/`.md` do balde `arquivos`. */
export async function lerTexto(arquivo: Pick<ArquivoDoPerfil, 'caminho'>): Promise<string> {
  const { data, error } = await supabase.storage.from(BALDE_DE_ARQUIVOS).download(arquivo.caminho);
  if (error || !data) throw error ?? new Error('Não foi possível abrir o texto.');
  return data.text();
}

/**
 * Grava o texto por cima do mesmo arquivo. O gatilho de `storage.objects` acerta a cota pela
 * diferença de tamanho (UPDATE), então salvar um texto maior não escapa da conta.
 */
export async function salvarTexto(arquivo: Pick<ArquivoDoPerfil, 'caminho' | 'nome'>, texto: string): Promise<void> {
  const tipo = /\.md$/i.test(arquivo.nome) ? 'text/markdown' : 'text/plain';
  const { error } = await supabase.storage.from(BALDE_DE_ARQUIVOS).upload(
    arquivo.caminho,
    new Blob([texto], { type: `${tipo};charset=utf-8` }),
    { upsert: true, contentType: `${tipo};charset=utf-8` },
  );
  if (error) throw error;
}

/**
 * A versão do arquivo no Storage agora: muda a cada gravação. `null` se ele não está mais neste
 * caminho (outra pessoa renomeou ou apagou). O editor guarda a de quando abriu e confere antes de
 * salvar: dois membros no mesmo texto, venceria o último sem ninguém saber (QA dos Arquivos, 26/09).
 */
export async function versaoDoArquivo(arquivo: Pick<ArquivoDoPerfil, 'caminho'>): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BALDE_DE_ARQUIVOS).info(arquivo.caminho);
  if (error || !data) return null;
  return String(data.version || data.etag || data.lastModified || data.updatedAt || '');
}

/**
 * Grava um arquivo pronto (o .docx do editor) por cima do mesmo caminho. A cota segue o gatilho. O
 * app manda os bytes (o `Blob` do React Native sobe vazio).
 */
export async function salvarArquivo(arquivo: Pick<ArquivoDoPerfil, 'caminho'>, conteudo: Blob | ArrayBuffer, tipo: string): Promise<void> {
  const { error } = await supabase.storage.from(BALDE_DE_ARQUIVOS).upload(arquivo.caminho, conteudo, { upsert: true, contentType: tipo });
  if (error) throw error;
}

// ─── A letra e a ficha de cada música ──────────────────────────────────────────

/**
 * Os dados de uma música que viram DOCUMENTOS na pasta dela (Letra e Ficha técnica). Não são
 * arquivos: são colunas do projeto e da gravação principal (RPC `documentos_das_musicas`).
 */
export interface MusicaDoPerfil {
  projetoId: string;
  titulo: string;
  status: string | null;
  genero: string | null;
  lancamento: string | null;
  upc: string | null;
  observacoes: string | null;
  /** A gravação principal. Sem ela não há onde guardar a letra, o ISRC, o BPM e o tom. */
  versaoId: string | null;
  isrc: string | null;
  bpm: string | null;
  tom: string | null;
  duracao: string | null;
  letra: string | null;
  atualizadoEm: string | null;
}

interface LinhaDeDocumento {
  projeto_id: string; titulo: string | null; status: string | null; genero: string | null;
  lancamento: string | null; upc: string | null; observacoes: string | null; versao_id: string | null;
  isrc: string | null; bpm: string | null; tom: string | null; duracao: string | null;
  letra: string | null; atualizado_em: string | null;
}

export async function documentosDasMusicas(artistId: string): Promise<MusicaDoPerfil[]> {
  const { data, error } = await supabase.rpc('documentos_das_musicas', { p_artist_id: artistId });
  if (error) throw error;
  return ((data as LinhaDeDocumento[] | null) ?? []).map((l) => ({
    projetoId: l.projeto_id,
    titulo: l.titulo || 'Sem título',
    status: l.status,
    genero: l.genero,
    lancamento: l.lancamento,
    upc: l.upc,
    observacoes: l.observacoes,
    versaoId: l.versao_id,
    isrc: l.isrc,
    bpm: l.bpm,
    tom: l.tom,
    duracao: l.duracao,
    letra: l.letra,
    atualizadoEm: l.atualizado_em,
  }));
}

/** A letra mora na GRAVAÇÃO (`catalog_versions.lyrics`), como no editor da música. */
export async function salvarLetra(versaoId: string, letra: string): Promise<void> {
  const { data, error } = await supabase
    .from('catalog_versions')
    .update({ lyrics: letra, updated_at: new Date().toISOString() })
    .eq('id', versaoId)
    .select('id');
  if (error) throw error;
  // Negado pela RLS (a trava de assinatura, a pessoa fora da equipe) é zero linhas, e não erro:
  // sem esta conta a tela dizia "Salvo." e a letra sumia ao recarregar (QA dos Arquivos, 26/09).
  if (!data?.length) throw new Error('A letra não foi salva.');
}

/** Os campos que a ficha técnica do módulo Arquivos edita. */
export interface CamposEditaveisDaFicha {
  titulo: string;
  status: string | null;
  genero: string | null;
  lancamento: string | null;
  upc: string | null;
  observacoes: string | null;
  isrc: string | null;
}

/**
 * Grava a ficha técnica, campo por campo, na coluna certa.
 *
 * ⚠️ UPDATE SÓ DOS CAMPOS DA FICHA, e não o `saveCatalogProjectFromForm`: aquele reescreve a
 * música inteira (capa, responsável, `title || 'Sem título'`...), e uma edição da ficha apagaria o
 * que ela não mostra.
 *
 * ⚠️ BPM E TOM NÃO SE EDITAM AQUI. São da gravação e moram na barra do editor; a ficha antiga que
 * os repetia sobrescrevia a barra (ver o comentário do `saveCatalogProjectFromForm`). A duração
 * vem do áudio. Os três aparecem na ficha, só para ler.
 */
export async function salvarFichaDaMusica(musica: Pick<MusicaDoPerfil, 'projetoId' | 'versaoId'>, campos: CamposEditaveisDaFicha): Promise<void> {
  const agora = new Date().toISOString();
  const vazio = (v: string | null | undefined) => (v && v.trim() ? v.trim() : null);
  const { data, error } = await supabase
    .from('catalog_projects')
    .update({
      title: campos.titulo.trim() || 'Sem título',
      status: campos.status,
      genre: vazio(campos.genero),
      release_date: vazio(campos.lancamento),
      upc: vazio(campos.upc),
      details: vazio(campos.observacoes),
      updated_at: agora,
    })
    .eq('id', musica.projetoId)
    .select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('A ficha não foi salva.');
  if (musica.versaoId) {
    const { data: gravacao, error: erroDaGravacao } = await supabase
      .from('catalog_versions')
      .update({ isrc: vazio(campos.isrc), updated_at: agora })
      .eq('id', musica.versaoId)
      .select('id');
    if (erroDaGravacao) throw erroDaGravacao;
    if (!gravacao?.length) throw new Error('O ISRC não foi salvo.');
  }
}

/**
 * Apaga um arquivo do balde.
 *
 * ⚠️ NÃO confere aqui se o arquivo está em uso, e é de propósito: quem confere é a policy
 * `catalog_delete_sem_uso`, no banco. Uma checagem no cliente seria conselho, não regra — e a
 * regra precisa valer também para quem chama a API direto, senão uma música fica apontando para
 * um endereço morto e o tocador quebra sem explicação.
 *
 * A contabilidade se corrige sozinha: o gatilho de DELETE em `storage.objects` debita os bytes.
 */
export async function apagarArquivoDoPerfil(arquivo: Pick<ArquivoDoPerfil, 'caminho' | 'balde'>): Promise<void> {
  // `remove` do supabase-js não lança: o erro vem no retorno, e ignorá-lo diria "apagado" sobre
  // um arquivo que continua lá.
  const { data, error } = await supabase.storage.from(arquivo.balde).remove([arquivo.caminho]);
  if (error) throw error;
  // Recusado pela policy (o arquivo passou a ser usado por uma música entre a lista e o clique) é
  // uma lista vazia, sem erro: a tela dizia "apagado" e ele voltava no próximo carregamento.
  if (!data?.length) throw new Error('O arquivo não foi apagado.');
}

// As pastas que a pessoa cria e a da Nyta moram em `pastasDoPerfil`; quem já usa este módulo as
// alcança por aqui também.
export * from './pastasDoPerfil';
